import { useCallback } from "react";
import type { RenderNode } from "../../../util/src/layout-compiler/types.js";
import { useRegisterNodeElement } from "./CompiledLayoutRuntimeContext.js";
import {
  isRuntimeNodeDisabled,
  needsRuntimeButtonSemantics,
} from "./runtimeNodeInteractions.js";

/** Attributes on the existing root element; no extra wrapper or per-node listener. */
export function useRuntimeNodeProps(node: RenderNode) {
  const ref = useRuntimeElementRef(node);
  const disabled = isRuntimeNodeDisabled(node);
  const buttonSemantics = needsRuntimeButtonSemantics(node);
  return {
    ref,
    "data-layout-node-key": node.key ?? node.id,
    ...(node.clickable ? { "data-layout-clickable": "true" } : {}),
    ...(disabled ? { "aria-disabled": true as const } : {}),
    ...(buttonSemantics ? { role: "button", tabIndex: disabled ? -1 : 0 } : {}),
  };
}

/**
 * Registers the rendered DOM element for runtime effects and clip-path sync.
 */
export function useRuntimeElementRef(
  node: RenderNode,
): React.RefCallback<HTMLElement> {
  const registerNodeElement = useRegisterNodeElement();

  return useCallback(
    (element: HTMLElement | null) => {
      if (!registerNodeElement) {
        return;
      }

      const key = node.key ?? node.id;
      registerNodeElement(key, element, node);
    },
    [node, registerNodeElement],
  );
}
